import { getObjectUrl } from 'services/FieldService';
import { TextEllipsisWithTooltip } from 'components/Kizen/Table';
import LinkTextInline from 'components/Inputs/inline/LinkText';
import { getColumnEditable } from 'components/Fields/InlineTableFieldInput';
import { DEFAULT_FIELD_NAMES, FIELD_TYPES } from 'utility/constants';
import {
  CheckboxCell,
  CheckboxMenuCell,
  ColumnsIconLinkCell,
  SpacerCell,
} from 'pages/Common/components/BigTableCells';
import { getDataQAForInput } from 'components/Inputs/helpers';
import { CustomFieldHeadCell } from './CustomFieldCell';
import { SizedCell, SortableHeadCell } from './Table';
import { scrollFieldIntoView } from 'components/Fields/helpers';
import TextInputInline from 'components/Inputs/inline/TextInput';

import {
  getCellForFieldType,
  EmailCell,
  PhoneNumberCell,
  DynamicTagsCell,
  DateTimeCell,
} from 'components/Layout/BigTableLayout/InlineCells';
import { getColumnWidth } from 'components/Fields/InlineTableFieldInput';
import ClearSelectButton from 'components/Inputs/Select/ClearButton';
import NewRowMenuCell from 'components/Tables/Big/NewRowMenuCell';
import { snakeToCamelCase } from 'services/helpers';
import { getAdornments } from 'components/Inputs/Adornments/util';

const defaultFieldNameExceptions = {
  fullName: DEFAULT_FIELD_NAMES.firstName,
  [snakeToCamelCase(DEFAULT_FIELD_NAMES.mobilePhone)]:
    DEFAULT_FIELD_NAMES.mobilePhone,
};

export const getDefaultField = (name, fields) => {
  name = defaultFieldNameExceptions[name] || name;
  return fields.find((f) => f.isDefault && f.name === name);
};

export const getEditable = ({ access } = {}) => Boolean(access && access.edit);

const defaultFieldWidths = {
  fullName: 175,
  [DEFAULT_FIELD_NAMES.email]: 200,
  [DEFAULT_FIELD_NAMES.mobilePhone]: 125,
  [DEFAULT_FIELD_NAMES.titles]: 125,
  [DEFAULT_FIELD_NAMES.tags]: 250,
  [DEFAULT_FIELD_NAMES.created]: 150,
  [DEFAULT_FIELD_NAMES.updated]: 150,
};

const dataQaPrefix = 'inline-contact-';

const getBasicColumns = ({
  onSubmitContact,
  fields,
  serviceToUse,
  model,
  handleUpdateFieldOption,
  handleUpdateTableRecords,
  adornments,
}) => {
  const firstNameField = getDefaultField(DEFAULT_FIELD_NAMES.firstName, fields);
  const lastNameField = getDefaultField(DEFAULT_FIELD_NAMES.lastName, fields);
  const emailField = getDefaultField(DEFAULT_FIELD_NAMES.email, fields);
  const mobilePhoneField = getDefaultField(
    DEFAULT_FIELD_NAMES.mobilePhone,
    fields
  );
  const titlesField = getDefaultField(DEFAULT_FIELD_NAMES.titles, fields);
  const tagsField = getDefaultField(DEFAULT_FIELD_NAMES.tags, fields);
  const createdField = getDefaultField(DEFAULT_FIELD_NAMES.created, fields);
  const updatedField = getDefaultField(DEFAULT_FIELD_NAMES.updated, fields);

  const defaultCellProps = {
    onSubmit: onSubmitContact,
    serviceToUse,
    model,
    handleUpdateFieldOption,
    handleUpdateTableRecords,
    isCustomObjects: false,
    shouldFocusNext: false,
  };

  return {
    fullName: {
      cell: <SizedCell minWidth={`${defaultFieldWidths.fullName}px`} />,
      headCell: (
        <SortableHeadCell
          by="full_name"
          minWidth={`${defaultFieldWidths.fullName}px`}
        />
      ),
      meta: { editable: getEditable(firstNameField) },
      format: (_, data, column) => {
        const fields = [...Object.values(data.fields)];
        const first = fields?.find((x) => x.name === 'first_name')?.value;
        const last = fields?.find((x) => x.name === 'last_name')?.value;
        const fullName = [first, last].filter(Boolean).join(' ');
        const { id: entityId } = data;
        if (!column.meta.editable || !getEditable(data)) {
          return (
            <TextEllipsisWithTooltip
              type="link"
              to={entityId && `/client/${entityId}/details`}
            >
              {fullName}
            </TextEllipsisWithTooltip>
          );
        }
        const handleSubmit = async (value) => {
          const [firstName, ...lastNames] = (value || '').split(' ');
          const lastName = lastNames.join(' ');

          return onSubmitContact({
            column,
            entityId,
            values: [
              { field: firstNameField, value: firstName },
              { field: lastNameField, value: lastName },
            ],
          });
        };
        return entityId === 'new' ? (
          <TextInputInline
            isNew
            field={firstNameField}
            entityId={entityId}
            shouldFocusNext={false}
            value={fullName}
            onSubmit={(value) => value && handleSubmit(value)}
            onAutoFocus={scrollFieldIntoView}
            submitUnchanged={true}
            {...getDataQAForInput(
              'inline-contact-full_name',
              firstNameField.fieldType
            )}
          />
        ) : (
          <LinkTextInline
            field={firstNameField}
            entityId={entityId}
            type="link"
            shouldFocusNext={false}
            to={`/client/${entityId}/details`}
            value={fullName}
            onSubmit={handleSubmit}
            {...getDataQAForInput(
              'inline-contact-full_name',
              firstNameField.fieldType
            )}
          />
        );
      },
      field: firstNameField,
    },
    ...(firstNameField
      ? {
          [snakeToCamelCase(DEFAULT_FIELD_NAMES.firstName)]: {
            headCell: (
              <SortableHeadCell
                by={DEFAULT_FIELD_NAMES.firstName}
                minWidth={`${defaultFieldWidths.fullName}px`}
              />
            ),
            meta: { editable: getEditable(firstNameField) },
            bodyCell: {
              ...defaultCellProps,
              field: firstNameField,
              minWidth: `${defaultFieldWidths.fullName}px`,
              Component: getCellForFieldType(firstNameField.fieldType),
              meta: { editable: getEditable(firstNameField) },
              dataQaPrefix,
            },
            field: firstNameField,
          },
        }
      : {}),
    ...(lastNameField
      ? {
          [snakeToCamelCase(DEFAULT_FIELD_NAMES.lastName)]: {
            headCell: (
              <SortableHeadCell
                by={DEFAULT_FIELD_NAMES.lastName}
                minWidth={`${defaultFieldWidths.fullName}px`}
              />
            ),
            meta: { editable: getEditable(lastNameField) },
            bodyCell: {
              ...defaultCellProps,
              field: lastNameField,
              minWidth: `${defaultFieldWidths.fullName}px`,
              Component: getCellForFieldType(lastNameField.fieldType),
              meta: { editable: getEditable(lastNameField) },
              dataQaPrefix,
            },
            field: lastNameField,
          },
        }
      : {}),
    ...(emailField
      ? {
          [DEFAULT_FIELD_NAMES.email]: {
            headCell: (
              <SortableHeadCell
                minWidth={`${defaultFieldWidths[DEFAULT_FIELD_NAMES.email]}px`}
              />
            ),
            meta: { editable: getEditable(emailField) },
            bodyCell: {
              ...defaultCellProps,
              field: emailField,
              minWidth: `${defaultFieldWidths[DEFAULT_FIELD_NAMES.email]}px`,
              Component: EmailCell,
              meta: { editable: getEditable(emailField) },
              dataQaPrefix,
            },
            field: emailField,
          },
        }
      : {}),
    ...(mobilePhoneField
      ? {
          [snakeToCamelCase(DEFAULT_FIELD_NAMES.mobilePhone)]: {
            headCell: (
              <SortableHeadCell
                numeric
                by={DEFAULT_FIELD_NAMES.mobilePhone}
                minWidth={`${
                  defaultFieldWidths[DEFAULT_FIELD_NAMES.mobilePhone]
                }px`}
              />
            ),
            meta: { editable: getEditable(mobilePhoneField) },
            bodyCell: {
              ...defaultCellProps,
              field: mobilePhoneField,
              minWidth: `${defaultFieldWidths[DEFAULT_FIELD_NAMES.mobilePhone]}px`,
              adornments:
                adornments?.filter(
                  (a) => a?.field_type === mobilePhoneField.fieldType
                ) ?? [],
              Component: PhoneNumberCell,
              meta: { editable: getEditable(mobilePhoneField) },
              dataQaPrefix,
            },
            field: mobilePhoneField,
          },
        }
      : {}),
    ...(titlesField
      ? {
          [DEFAULT_FIELD_NAMES.titles]: {
            headCell: (
              <CustomFieldHeadCell
                shouldFocusNext={false}
                minWidth={`${defaultFieldWidths[DEFAULT_FIELD_NAMES.titles]}px`}
                hasSorting={false}
                field={titlesField}
              />
            ),
            meta: { editable: getEditable(titlesField) },
            bodyCell: {
              ...defaultCellProps,
              field: titlesField,
              minWidth: `${defaultFieldWidths[DEFAULT_FIELD_NAMES.titles]}px`,
              Component: DynamicTagsCell,
              fetchUrl: getObjectUrl('contacts'),
              meta: { editable: getEditable(titlesField) },
              dataQaPrefix,
            },
            field: titlesField,
          },
        }
      : {}),
    ...(tagsField
      ? {
          [DEFAULT_FIELD_NAMES.tags]: {
            headCell: (
              <CustomFieldHeadCell
                minWidth={`${defaultFieldWidths[DEFAULT_FIELD_NAMES.tags]}px`}
                hasSorting={false}
                field={tagsField}
              />
            ),
            meta: { editable: getEditable(tagsField) },
            bodyCell: {
              ...defaultCellProps,
              field: tagsField,
              minWidth: `${defaultFieldWidths[DEFAULT_FIELD_NAMES.tags]}px`,
              Component: DynamicTagsCell,
              fetchUrl: getObjectUrl('contacts'),
              meta: { editable: getEditable(tagsField) },
              dataQaPrefix,
            },
            field: tagsField,
          },
        }
      : {}),
    ...(createdField
      ? {
          [DEFAULT_FIELD_NAMES.created]: {
            headCell: (
              <SortableHeadCell
                numeric
                minWidth={`${
                  defaultFieldWidths[DEFAULT_FIELD_NAMES.created]
                }px`}
              />
            ),
            meta: { editable: false },
            bodyCell: {
              ...defaultCellProps,
              field: createdField,
              minWidth: `${defaultFieldWidths[DEFAULT_FIELD_NAMES.created]}px`,
              Component: DateTimeCell,
              adornments,
              meta: { editable: false },
              dataQaPrefix,
              showDateTooltip: true,
            },
            field: createdField,
          },
        }
      : {}),
    ...(updatedField
      ? {
          [DEFAULT_FIELD_NAMES.updated]: {
            headCell: (
              <SortableHeadCell
                numeric
                minWidth={`${
                  defaultFieldWidths[DEFAULT_FIELD_NAMES.updated]
                }px`}
              />
            ),
            meta: { editable: false },
            bodyCell: {
              ...defaultCellProps,
              field: updatedField,
              minWidth: `${defaultFieldWidths[DEFAULT_FIELD_NAMES.updated]}px`,
              Component: DateTimeCell,
              adornments,
              meta: { editable: false },
              dataQaPrefix,
              showDateTooltip: true,
            },
            field: updatedField,
          },
        }
      : {}),
  };
};

export const createColumns = ({
  fields,
  columnSettings,
  onSubmitContact,
  indeterminate,
  hasBulkActions,
  onSelectAction,
  categorizedFields,
  handleUpdateTableRecords,
  handleUpdateFieldOption,
  serviceToUse,
  model,
  adornments,
  t,
}) => {
  const fieldsById = fields.reduce(
    (collect, field) => ({
      ...collect,
      [field.id]: field,
    }),
    {}
  );

  const basicColumns = getBasicColumns({
    onSubmitContact,
    fields,
    serviceToUse,
    model,
    handleUpdateFieldOption, // kzn-8229
    handleUpdateTableRecords,
    adornments,
  });
  return [
    hasBulkActions
      ? {
          id: 'selection',
          cell: <CheckboxCell indeterminate={indeterminate} />,
          headCell: (
            <CheckboxMenuCell
              data-qa-selection-checkbox-menu
              indeterminate={indeterminate}
            />
          ),
        }
      : {
          id: 'spacer',
          headCell: <SpacerCell width="15px" />,
          cell: <SpacerCell width="15px" />,
        },
    ...columnSettings
      .filter(({ visible }) => visible)
      .map(({ id, label }) => {
        if (basicColumns[id]) {
          return {
            id,
            label,
            ...basicColumns[id],
          };
        }
        const field = fieldsById[id];
        const adornmentsByField = getAdornments(adornments, field);

        const fieldProps =
          field.fieldType === FIELD_TYPES.Relationship.type &&
          field.relation.fetchUrl === 'client'
            ? { categorizedFields }
            : {};

        return {
          id,
          label,
          headCell: (
            <CustomFieldHeadCell
              field={field}
              hasSorting={field.fieldType !== FIELD_TYPES.Relationship.type}
              minWidth={`${getColumnWidth(field, adornmentsByField)}px`}
            />
          ),
          bodyCell: {
            field,
            onSubmit: onSubmitContact,
            fetchUrl: getObjectUrl('contacts'),
            serviceToUse: serviceToUse,
            model,
            handleUpdateFieldOption, // kzn-8229
            handleUpdateTableRecords,
            displayMaskedValue: true,
            minWidth: `${getColumnWidth(field, adornmentsByField)}px`,
            menuLeftButton: <ClearSelectButton />,
            Component: getCellForFieldType(field.fieldType), // cellMap.get(field.fieldType) || MissedCell,
            adornments: adornmentsByField,
            isCustomObjects: false,
            shouldFocusNext: false,
            dataQaPrefix,
            fieldProps,
          },
          field: field,
          object: model,
          meta: { editable: getEditable(field) && getColumnEditable(field) },
        };
      }),
    {
      id: 'columns',
      headCell: <ColumnsIconLinkCell zIndex={2} />,
      bodyCell: {
        isMenuCell: true,
        Component: NewRowMenuCell,
        isCustomObjects: true,
        title: t('Edit Record'),
        onSelect: onSelectAction,
      },
    },
  ];
};
